import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BrandMark, RailLink } from '../primitives';
import { getOrgRailSurfaces, PERSONAL_RAIL_SURFACES, RAIL_SURFACES } from './surfaces';

export interface PenroseLeftRailProps {
  /** Onboarding strip — total step count. Omit to hide the strip. */
  onboardingTotal?: number;
  /** Onboarding strip — completed step count. */
  onboardingDone?: number;
  /** Sign-out click — host shell owns the supabase call + redirect. */
  onSignOut?: () => void;
  /**
   * Per-link badge counts, keyed by surface key. Renders inside the
   * trailing `<RailLink badge>` slot when present.
   */
  badges?: Partial<Record<(typeof RAIL_SURFACES)[number]['key'], ReactNode>>;
  /**
   * Optional override for the active-rail key — useful in dev fixtures /
   * snapshot specs whose route doesn't match any rail surface. Production
   * compositions should leave this undefined and let `useLocation()` drive
   * the active state.
   */
  activeKey?: (typeof RAIL_SURFACES)[number]['key'];
}

const RAIL_ICONS: Record<(typeof RAIL_SURFACES)[number]['key'], ReactNode> = {
  dashboard: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <rect x="2" y="2" width="5" height="6" rx="1" />
      <rect x="9" y="2" width="5" height="3" rx="1" />
      <rect x="2" y="10" width="5" height="4" rx="1" />
      <rect x="9" y="7" width="5" height="7" rx="1" />
    </svg>
  ),
  projects: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
      <path d="M2 6h12M7 6v7.5" />
    </svg>
  ),
  agent: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M2.5 3.5h11v7h-7l-3 3z" strokeLinejoin="round" />
      <path d="M5 6h6M5 8.5h4" strokeLinecap="round" />
    </svg>
  ),
  documents: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M3.5 2h6L13 5.5V14H3.5z" />
      <path d="M9.5 2v3.5H13" />
      <path d="M5.5 8.5h5M5.5 11h4" strokeLinecap="round" />
    </svg>
  ),
  'work-items': (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <rect x="2" y="2.5" width="12" height="11" rx="1" />
      <path d="M6 2.5v11M10 2.5v11" />
      <path d="M3.25 5h1.5M7.25 7.5h1.5M11.25 10h1.5" strokeLinecap="round" />
    </svg>
  ),
  members: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <circle cx="6" cy="6" r="2.4" />
      <path d="M2 13.5c.6-2 2-3 4-3s3.4 1 4 3" />
      <circle cx="11.5" cy="6.5" r="1.8" />
      <path d="M10.5 13.5c.4-1.5 1.5-2.3 3-2.3" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.1" aria-hidden="true">
      <g transform="scale(0.666667)">
        <circle cx="12" cy="12" r="3" />
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      </g>
    </svg>
  ),
  todos: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <rect x="2.5" y="2.5" width="11" height="11" rx="2" />
      <path d="M5.5 8l1.8 1.8L11 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
};

/**
 * Canonical signed-in left rail — populates the `<PenroseShell rail>` slot.
 *
 * Top: brand strip (Penrose mark + wordmark).
 * Middle: nav section composing `<RailLink>` for the rail surfaces;
 *   active state is derived from `useLocation()` and projected via
 *   `aria-current="page"` per the primitive's contract.
 * Foot: optional onboarding strip + sign-out button. Org switching and
 *   identity live in the header (`<PenroseTopHeader>`); sign-out wiring
 *   is the host shell's responsibility.
 */
export function PenroseLeftRail({
  onboardingTotal,
  onboardingDone = 0,
  onSignOut,
  badges,
  activeKey
}: PenroseLeftRailProps) {
  const { pathname } = useLocation();

  const showOnboarding =
    typeof onboardingTotal === 'number' && onboardingTotal > 0;
  const pct = showOnboarding
    ? Math.round((onboardingDone / onboardingTotal!) * 100)
    : 0;

  return (
    <>
      <div className="pn-app-rail-brand">
        <BrandMark className="pn-app-rail-brand-mark" />
        <span className="pn-app-rail-brand-word">PENROSE</span>
      </div>

      <nav className="pn-app-rail-nav" aria-label="Workspace">
        {getOrgRailSurfaces().map((s) => {
          const active =
            activeKey != null
              ? activeKey === s.key
              : pathname === s.path || pathname.startsWith(`${s.path}/`);
          return (
            <RailLink
              key={s.key}
              to={s.path}
              icon={RAIL_ICONS[s.key]}
              badge={badges?.[s.key]}
              active={active}
              data-testid={`rail-${s.key}`}
            >
              {s.label}
            </RailLink>
          );
        })}
        <div
          className="pn-app-rail-divider"
          role="separator"
          aria-hidden="true"
          data-testid="rail-personal-divider"
        />
        {PERSONAL_RAIL_SURFACES.map((s) => {
          const active =
            activeKey != null
              ? activeKey === s.key
              : pathname === s.path || pathname.startsWith(`${s.path}/`);
          return (
            <RailLink
              key={s.key}
              to={s.path}
              icon={RAIL_ICONS[s.key]}
              badge={badges?.[s.key]}
              active={active}
              data-testid={`rail-${s.key}`}
            >
              {s.label}
            </RailLink>
          );
        })}
      </nav>

      <div className="pn-app-rail-spacer" />

      <div className="pn-app-rail-foot">
        {showOnboarding ? (
          <div className="pn-app-rail-onboarding" data-testid="rail-onboarding">
            <span>
              <strong>Onboarding</strong>{' '}
              <span className="pn-app-rail-onboarding-count">
                {onboardingDone} of {onboardingTotal}
              </span>
            </span>
            <span className="pn-app-rail-onboarding-pct">{pct}%</span>
          </div>
        ) : null}
        <Link
          to="/download"
          className="pn-app-rail-download"
          data-testid="rail-download"
        >
          <span className="pn-app-rail-download-ico" aria-hidden="true">
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
            >
              <path d="M8 2.5v7" strokeLinecap="round" />
              <path d="M5.5 7 8 9.5 10.5 7" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3.5 13.5h9" strokeLinecap="round" />
            </svg>
          </span>
          <span className="pn-app-rail-download-label">Download IDE</span>
        </Link>
        <button
          type="button"
          className="pn-app-rail-signout"
          onClick={onSignOut}
          data-testid="rail-signout"
          aria-label="Sign out"
        >
          <span className="pn-app-rail-signout-ico" aria-hidden="true">
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
            >
              <path d="M9 3.5H4v9h5" />
              <path d="M11 5l3 3-3 3M7 8h7" strokeLinecap="round" />
            </svg>
          </span>
          <span className="pn-app-rail-signout-label">Sign out</span>
        </button>
      </div>
    </>
  );
}
